/**
 * Landing Page Component
 * 
 * Shown to unauthenticated users. Provides sign in options
 * to access the main application.
 */

import React, { useState } from 'react';
import AuthModal from './AuthModal';
import { KnodsIcon } from './ui/KnodsIcon';
import './LandingPage.css';

// Unique model logos for the scrolling strip
const MODEL_LOGOS = [
  { src: '/icons/models/openai.png', alt: 'OpenAI' },
  { src: '/icons/models/claude.png', alt: 'Claude' },
  { src: '/icons/models/kimi.svg', alt: 'Kimi' },
  { src: '/icons/models/gemini.png', alt: 'Google Gemini' },
  { src: '/icons/models/flux.png', alt: 'FLUX' },
  { src: '/icons/models/grok.svg', alt: 'Grok' },
  { src: '/icons/models/kling.png', alt: 'Kling' },
  { src: '/icons/models/wan.png', alt: 'Wan' },
  { src: '/icons/models/veo.png', alt: 'VEO' },
  { src: '/icons/models/ltx.png', alt: 'LTX' },
  { src: '/icons/models/qwen.png', alt: 'Qwen' },
  { src: '/icons/models/z-image.png', alt: 'Z-Image' },
];

export const LandingPage: React.FC = () => {
  const [showAuthModal, setShowAuthModal] = useState(false);

  return (
    <div className="landing-page">
      <div className="landing-content">
        {/* Logo/Brand */}
        <div className="landing-brand">
          <div className="landing-logo">
            <KnodsIcon />
          </div>
          <h1 className="landing-title">Knods</h1>
          <p className="landing-subtitle">
            All your AI models in one canvas.<br />
            Connect them. Chain them. Create.
          </p>
        </div>

        {/* Capabilities */}
        <div className="landing-capabilities">
          <span className="landing-cap landing-cap-text">Text</span>
          <span className="landing-cap-dot" />
          <span className="landing-cap landing-cap-image">Images</span>
          <span className="landing-cap-dot" />
          <span className="landing-cap landing-cap-video">Video</span>
          <span className="landing-cap-dot" />
          <span className="landing-cap landing-cap-voice">Sound</span>
        </div>

        {/* Model Logo Strip — scrolling */}
        <div className="landing-models">
          <p className="landing-models-label">30+ AI models from</p>
          <div className="landing-models-track">
            <div className="landing-models-scroll">
              {/* Double the logos for seamless infinite scroll */}
              {[...MODEL_LOGOS, ...MODEL_LOGOS].map((logo, i) => (
                <div className="landing-model-logo" key={`${logo.alt}-${i}`}>
                  <img src={logo.src} alt={logo.alt} />
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* CTA */}
        <div className="landing-cta">
          <button 
            className="landing-button-primary"
            onClick={() => setShowAuthModal(true)}
          >
            Start Creating
          </button>
          <p className="landing-cta-note">50 free credits. No card needed.</p>
        </div>
      </div>

      {/* Background video */}
      <video
        className="landing-bg-video"
        autoPlay
        muted
        loop
        playsInline
        preload="auto"
        aria-hidden="true"
      >
        <source src="/videos/landing-bg.webm" type="video/webm" />
        <source src="/videos/landing-bg.mp4" type="video/mp4" />
      </video>
      <div className="landing-bg-overlay" />

      {/* Background decoration */}
      <div className="landing-bg-grid" />
      <div className="landing-bg-glow" />

      {/* Footer with Legal Links */}
      <footer className="landing-footer">
        <div className="landing-footer-content">
          <a href="/terms-of-service.html" target="_blank" rel="noopener noreferrer">
            Terms of Service
          </a>
          <span className="landing-footer-divider">·</span>
          <a href="/privacy-policy.html" target="_blank" rel="noopener noreferrer">
            Privacy Policy
          </a>
          <span className="landing-footer-divider">·</span>
          <a href="/cookies.html" target="_blank" rel="noopener noreferrer">
            Cookie Policy
          </a>
        </div>
      </footer>

      {/* Auth Modal */}
      <AuthModal
        isOpen={showAuthModal}
        onClose={() => setShowAuthModal(false)}
      />
    </div>
  );
};

export default LandingPage;
